<template>
  <!-- Empty placeholder so a partial page still prints 4 evenly sized slots -->
  <div
    v-if="empty"
    class="tag-item border-dashed border-gray-300 flex items-center justify-center text-gray-400"
  >
    <div class="text-center">
      <div>Empty Slot</div>
      <div class="text-xs">{{ emptyLabel }}</div>
    </div>
  </div>

  <div v-else class="tag-item">
    <!-- Header -->
    <div class="text-center mb-2 tag-header">
      <h2 class="event-name" :class="eventNameSizeClass">{{ eventName }}</h2>
      <h3 v-if="showName" class="font-semibold show-name mt-0.5" :class="showNameSizeClass">{{ showName }}</h3>
    </div>

    <!-- Animal/Exhibit ID and Entry Number on same line -->
    <div class="flex justify-between items-center text-base font-bold mb-2">
      <div class="text-left flex-1 mr-2 animal-exhibit-id">
        {{ entryDetails.animal_id || entryDetails.name || 'Exhibit' }}
        <span v-if="showNickname" class="text-sm font-normal">
          ({{ entryDetails.nickname }})
        </span>
      </div>
      <div class="text-right flex-shrink-0 entry-number">
        Entry #: {{ entryNumber }}
      </div>
    </div>

    <!-- Division & Class -->
    <div class="mb-3 division-class-section">
      <div v-if="entryDetails.division" class="text-sm division-line"><strong>Division:</strong> {{ entryDetails.division }}</div>
      <div class="text-sm class-line"><strong>Class:</strong> {{ className }}</div>
    </div>

    <!-- Description & Additional Fields -->
    <div class="border-t border-gray-300 pt-2 mb-3 description-fields-section">
      <div v-if="entryDetails.description" class="text-sm mb-2">
        <strong>Description:</strong> {{ entryDetails.description }}
      </div>

      <div class="text-sm space-y-1">
        <div v-for="(f, k) in fields" :key="k">
          <strong>{{ f.label }}:</strong> {{ f.value }}
        </div>
      </div>
    </div>

    <!-- Fold Line -->
    <div class="border-t border-dashed border-gray-500 pt-2 pb-2 bg-gray-50">
      <div class="text-xs text-gray-600 text-center mb-2">
        FOLD HERE TO COVER EXHIBITOR INFORMATION
      </div>
      <div class="text-sm exhibitor-line">
        <strong>Exhibitor:</strong> {{ exhibitorLabel }}
      </div>
    </div>

    <!-- Fold Up Area -->
    <div class="bg-white py-4 flex items-center justify-center border-dashed border-l border-r border-gray-400 fold-up-area">
      <div class="text-xs text-gray-400 text-center">
        <div>FOLD UP AREA</div>
        <div>↑ Fold this section up to cover exhibitor information ↑</div>
      </div>
    </div>

    <!-- Claim Tag -->
    <div class="border-t-2 border-dashed border-gray-500 bg-gray-100 p-2 claim-tag-section">
      <div class="text-xs text-gray-600 text-center mb-1">
        ✂ CLAIM TAG ✂
      </div>
      <div class="text-sm">
        <div class="font-semibold claim-tag-line">{{ claimTagTitle }}</div>
        <div class="claim-tag-line">{{ exhibitorLabel }}</div>
        <div class="claim-tag-line"><strong>Entry #:</strong> {{ entryNumber }}</div>
      </div>
    </div>
  </div>
</template>

<script>
// Shared 4-per-page printable tag. Used by both the per-show static tags page
// and the per-exhibitor tags page so the printed output stays identical.
export default {
  name: 'PrintTag',
  props: {
    // { exhibitor: {...}, entry: {...} }
    tag: {
      type: Object,
      default: null
    },
    eventName: {
      type: String,
      default: ''
    },
    showName: {
      type: String,
      default: ''
    },
    // Pre-resolved extra fields: [{label, value}]
    fields: {
      type: Array,
      default: () => []
    },
    empty: {
      type: Boolean,
      default: false
    },
    emptyLabel: {
      type: String,
      default: ''
    }
  },
  computed: {
    entry () {
      return (this.tag && this.tag.entry) || {}
    },
    exhibitor () {
      return (this.tag && this.tag.exhibitor) || {}
    },
    entryDetails () {
      return this.entry.entry_details || {}
    },
    entryNumber () {
      return this.entry.entry_number || 'N/A'
    },
    className () {
      return (this.entry.class && this.entry.class.className) || ''
    },
    exhibitorLabel () {
      const ex = this.exhibitor
      const name = [ex.last_name, ex.first_name].filter(Boolean).join(', ')
      return [ex.exhibitor_number, name].filter(Boolean).join(' - ')
    },
    // The claim tag leads with the show name; fall back to the event name
    // when a page has no per-show context.
    claimTagTitle () {
      return this.showName || this.eventName
    },
    showNickname () {
      const d = this.entryDetails
      return !!d.nickname && d.nickname !== d.animal_id && d.nickname !== d.name
    },
    // Scale the header text down for long names so the tag header stays two lines
    eventNameSizeClass () {
      const len = (this.eventName || '').length
      if (len > 60) return 'text-sm'
      if (len > 40) return 'text-base'
      return 'text-lg'
    },
    showNameSizeClass () {
      const len = (this.showName || '').length
      if (len > 45) return 'text-sm'
      return 'text-base'
    }
  }
}
</script>

<style scoped>
.tag-item {
  border: 2px solid #d1d5db;
  padding: 1rem;
  background: white;
  display: flex;
  flex-direction: column;
  min-height: 400px;
}

/* Header — clamped so long event/show names can't push the tag content down */
.tag-header {
  min-height: 3.5rem;
  overflow: hidden;
  flex-shrink: 0;
}

.event-name,
.show-name {
  line-height: 1.15;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.event-name {
  -webkit-line-clamp: 2;
}

.show-name {
  -webkit-line-clamp: 1;
}

/* Fixed height sections for consistent alignment.
   Sized for 3 clamped lines (division 1 + class 2) so nothing clips mid-line. */
.division-class-section {
  min-height: 3.3rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

/* Division gets one line, class gets up to two — both truncate at a whole
   line instead of being cut in half by the section's fixed height */
.division-line,
.class-line {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  line-height: 1.25;
}

.division-line {
  -webkit-line-clamp: 1;
}

.class-line {
  -webkit-line-clamp: 2;
}

.description-fields-section {
  min-height: 6rem;
  overflow: hidden;
  flex: 1;
}

/* Animal/Exhibit ID and Entry Number layout */
.animal-exhibit-id {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 60%;
}

.entry-number {
  white-space: nowrap;
  min-width: 35%;
}

/* Single-line exhibitor rows — never wrap, truncate instead */
.exhibitor-line,
.claim-tag-line {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.claim-tag-section {
  flex-shrink: 0;
}

@media print {
  .tag-item {
    border: none !important;
    padding: 0.15in;
    min-height: 5in;
    height: 5in;
    overflow: hidden;
  }

  /* Fixed heights for print alignment */
  .tag-header {
    min-height: 0.75in !important;
    height: 0.75in !important;
    overflow: hidden;
  }

  /* 3 lines of 0.875rem text at 1.25 line-height ≈ 0.55in — the 0.6in box
     holds all three whole lines, so the clamps decide what's dropped */
  .division-class-section {
    min-height: 0.6in !important;
    height: 0.6in !important;
    overflow: hidden;
  }

  .division-line,
  .class-line {
    display: -webkit-box !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    line-height: 1.25 !important;
  }

  .division-line {
    -webkit-line-clamp: 1 !important;
  }

  .class-line {
    -webkit-line-clamp: 2 !important;
  }

  /* Absorbs the leftover space so the claim tag always lands inside the 5in tag */
  .description-fields-section {
    min-height: 0.8in !important;
    height: auto !important;
    flex: 1 1 auto;
    overflow: hidden;
  }

  .fold-up-area {
    padding-top: 0.12in !important;
    padding-bottom: 0.12in !important;
  }

  /* Ensure truncation works on print */
  .animal-exhibit-id {
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    max-width: 60% !important;
  }

  .entry-number {
    white-space: nowrap !important;
    min-width: 35% !important;
  }

  .exhibitor-line,
  .claim-tag-line {
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  /* Hide borders on print except dashed lines */
  .border-t {
    border-top: none !important;
  }

  .border-l {
    border-left: none !important;
  }

  .border-r {
    border-right: none !important;
  }

  .border-b {
    border-bottom: none !important;
  }

  /* Keep dashed lines for cutting */
  .border-dashed {
    border-style: dashed !important;
    border-color: #666 !important;
  }
}
</style>
